import { Action, Location, State, To, TransitionEvent } from '../types';
import { createSubscribers } from '../utils';

import { createBrowserBackend } from './backends/browser.backend';
import { createHashBackend, HashHistoryOptions } from './backends/hash.backend';
import { createMemoryBackend, MemoryHistoryOptions } from './backends/memory.backend';
import { HistoryBackend } from './backends/types';

const subscribers = createSubscribers<TransitionEvent>();
let action: Action = 'POP';

// The single active history backend. Created lazily, so merely importing
// navigation-core has NO side effects (no listener registered, no history.state
// written) — essential for an embeddable SDK. The default is the browser backend;
// initHashHistory() swaps in the hash backend before the app boots, and the SDK's
// dispose() restores the default via disposeHistory().
let currentBackend: HistoryBackend | undefined;

// Passed to every backend: it must call this after a write, and on user POP, to
// fire the transition. Kept here (not in the backend) so subscribers and the
// `action` snapshot stay backend-agnostic.
function notify(nextAction: Action) {
    action = nextAction;
    subscribers.call({ action, location: getBackend().getLocation() });
}

function getBackend(): HistoryBackend {
    if (!currentBackend) {
        currentBackend = createBrowserBackend(notify);
    }
    return currentBackend;
}

function installHistoryBackend(backend: HistoryBackend) {
    currentBackend?.dispose();
    currentBackend = backend;
}

function initHashHistory(options?: HashHistoryOptions) {
    // Module-load calls (no options) are idempotent. A call WITH options — e.g.
    // createSDK threading embedder config (hashPrefix / backButton) — re-installs
    // the backend so the options take effect. Safe because it runs before any
    // navigation (the re-install just re-seeds from the current hash).
    if (!options && currentBackend?.kind === 'hash') {
        return;
    }
    installHistoryBackend(createHashBackend(notify, options));
}

// Install the in-memory backend (no-URL mode). Called explicitly from createSDK
// for transient/modal embeds; the app also points the routing layer's location
// source at this backend so URL-model reads resolve against the in-memory stack.
function initMemoryHistory(options?: MemoryHistoryOptions) {
    installHistoryBackend(createMemoryBackend(notify, options));
}

// Tear down the active backend (remove its listeners, release its state) and
// reset to the lazy default. Wired into the SDK's dispose() so the hash hijack
// does not outlive the SDK: afterwards, host-page hash changes no longer drive
// the (unmounted) SDK. Subscribers are intentionally NOT drained here — they are
// owned by the still-running navigation saga, not by the history backend.
function disposeHistory() {
    currentBackend?.dispose();
    currentBackend = undefined;
}

// --- Public API (thin delegation to the active backend) ---

function pushState(to: To, state: State = {}) {
    getBackend().push(to, state);
}

function replace(to: To, state: State = {}, nextAction: Action = 'REPLACE') {
    getBackend().replace(to, state, nextAction);
}

function replaceState(to: To, state: State = {}) {
    replace(to, state, 'REPLACE');
}

function replaceStateWithReload(to: To, state: State = {}) {
    replace(to, state, 'REPLACE_RELOAD');
}

// Relative traversal delegates to the active backend: browser/hash call
// window.history (the native popstate/hashchange then drives POP); memory moves
// its in-memory index and emits POP directly.
function historyGo(delta: number) {
    getBackend().go(delta);
}

function historyBack() {
    getBackend().go(-1);
}

function historyForward() {
    getBackend().go(1);
}

function subscribeToTransition(event: TransitionEvent) {
    subscribers.push(event);
}

function unsubscribeFromTransition(event: TransitionEvent) {
    subscribers.remove(event);
}

function getLocation(): Location {
    return getBackend().getLocation();
}

export {
    action,
    disposeHistory,
    getLocation,
    historyBack,
    historyForward,
    historyGo,
    initHashHistory,
    initMemoryHistory,
    pushState,
    replaceState,
    replaceStateWithReload,
    subscribeToTransition,
    unsubscribeFromTransition,
};

export type { HashHistoryOptions } from './backends/hash.backend';
export type { MemoryHistoryOptions } from './backends/memory.backend';
